import { useState, useCallback } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { useTranslation, Trans } from "react-i18next";
import { motion } from "motion/react";
import { transitionSoft, viewportOnce } from "../animations";

const responsive = {
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 3000 },
    items: 1,
  },
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
  },
};

const SLIDE_COUNT = 3;

const floatLoop = {
  y: [0, -10, 0],
};

function StoreBadge({ children }) {
  return (
    <motion.li whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}>
      {children}
    </motion.li>
  );
}

export default function Selider() {
  const { t, i18n } = useTranslation();
  const playStoreHl = i18n.language?.split("-")[0] === "en" ? "en" : "ru";
  const [activeSlide, setActiveSlide] = useState(0);

  const handleAfterChange = useCallback((_, state) => {
    setActiveSlide(state.currentSlide % SLIDE_COUNT);
  }, []);

  return (
    <motion.div
      className="m-auto px-4 pb-8 md:pb-10 lg:pb-12 pt-16 lg:max-w-[1024px] xl:max-w-[1280px]"
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={{ ...transitionSoft, duration: 0.55 }}
    >
      <Carousel
        infinite
        showDots
        arrows={false}
        responsive={responsive}
        afterChange={handleAfterChange}
        transitionDuration={450}
        customTransition="transform 450ms cubic-bezier(0.22, 1, 0.36, 1)"
      >
        <div className="flex flex-col justify-around md:flex-row md:justify-between lg:h-[320px] xl:h-[400px]">
          <div className="sliderStyle w-[353px] md:w-[400px] xl:w-[540px]">
            <h3 className="titleStyleSlider pb-[28px] text-center text-[18px] font-medium uppercase leading-[39px] text-[#DA0011] md:ml-[0] md:text-left md:text-[26px] lg:text-[30px] xl:text-[36px] ml-[50px]">
              {t("slider.slide1.title")}
            </h3>
            <p className="textStyleSlider bold pb-[30px] text-center text-[18px] font-normal leading-[21px] md:ml-[0] md:text-left md:text-[22px] ml-[40px]">
              <Trans
                i18nKey="slider.slide1.body"
                components={{ bold: <b /> }}
              />
            </p>
            <ul className="flex justify-end md:justify-start">
              <StoreBadge>
                <a
                  href={`https://play.google.com/store/apps/details?id=tj.fmfb.mobile&hl=${playStoreHl}&gl=US`}
                >
                  <img src="./img/googlePlay2.svg" alt="" />
                </a>
              </StoreBadge>
              <StoreBadge>
                <a
                  href="https://itunes.apple.com/app/id1641458468"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src="./img/appStore.svg" alt="" />
                </a>
              </StoreBadge>
              <StoreBadge>
                <a href="./doc/app-release.apk" download>
                  <img src="./img/apk.svg" alt="" />
                </a>
              </StoreBadge>
            </ul>
          </div>
          <div className="contents">
            <motion.img
              className="ml-[30px] mt-[10px] w-[280px] md:ml-[0] md:mt-[0px] md:w-[300px] lg:w-[400px] xl:w-[583px]"
              src="./img/mobile.svg"
              alt=""
              animate={activeSlide === 0 ? floatLoop : { y: 0 }}
              transition={{
                duration: 4.8,
                repeat: activeSlide === 0 ? Infinity : 0,
                ease: "easeInOut",
              }}
            />
          </div>
        </div>
        <div className="flex flex-col justify-around md:flex-row md:justify-between lg:h-[320px] xl:h-[400px]">
          <div className="sliderStyle w-[353px] md:w-[400px] xl:w-[540px]">
            <h3 className="titleStyleSlider pb-[28px] text-center text-[18px] font-medium uppercase leading-[39px] text-[#DA0011] md:ml-[0] md:text-left md:text-[26px] lg:text-[30px] xl:text-[36px] ml-[50px]">
              {t("slider.slide2.title")}
            </h3>
            <p className="textStyleSlider pb-[20px] text-center text-[18px] font-normal leading-[21px] md:ml-[0] md:pb-[71px] md:text-left md:text-[22px] ml-[40px]">
              <Trans
                i18nKey="slider.slide2.body"
                components={{ bold: <b /> }}
              />
            </p>
          </div>
          <div className="contents">
            <motion.img
              className="ml-[80px] w-[180px] md:mb-[20px] md:ml-[0] md:w-[220px] lg:w-[220px] xl:w-[270px]"
              src="./img/shtrikh.svg"
              alt=""
              animate={activeSlide === 1 ? floatLoop : { y: 0 }}
              transition={{
                duration: 5.2,
                repeat: activeSlide === 1 ? Infinity : 0,
                ease: "easeInOut",
              }}
            />
          </div>
        </div>
        <div className="flex flex-col justify-around md:flex-row md:justify-between lg:h-[320px] xl:h-[400px]">
          <div className="sliderStyle w-[353px] md:w-[400px] xl:w-[540px]">
            <h3 className="titleStyleSlider pb-[28px] text-center text-[18px] font-medium uppercase leading-[39px] text-[#DA0011] md:ml-[0] md:text-left md:text-[26px] lg:text-[30px] xl:text-[36px] ml-[50px]">
              {t("slider.slide3.title")}
            </h3>
            <p className="textStyleSlider pb-[20px] text-center text-[18px] font-normal leading-[21px] md:ml-[0] md:pb-[71px] md:text-left md:text-[22px] ml-[40px]">
              <Trans
                i18nKey="slider.slide3.body"
                components={{ bold: <b /> }}
              />
            </p>
          </div>
          <div className="contents">
            <motion.img
              className="redProfileStyle ml-[70px] w-[240px] md:ml-[0] md:w-[280px] lg:w-[300px] xl:w-[400px]"
              src="./img/redProfile.svg"
              alt=""
              animate={activeSlide === 2 ? floatLoop : { y: 0 }}
              transition={{
                duration: 5,
                repeat: activeSlide === 2 ? Infinity : 0,
                ease: "easeInOut",
              }}
            />
          </div>
        </div>
      </Carousel>
    </motion.div>
  );
}
